import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtNGN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Gift, Lock } from "lucide-react";
import { motion } from "framer-motion";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh, authenticate } = useAuth();
  const reference = params.get("reference");
  const isSignup = params.get("flow") === "signup";
  const isMock = params.get("mock") === "1";
  const mockAmount = Number(params.get("amount")) || 3000;
  const mockPurpose = params.get("purpose") || "membership";
  const mockLabel = params.get("label") || (mockPurpose === "growth_funding" ? "Growth Wallet top-up" : "AR Club Membership (one-time)");
  const [phase, setPhase] = useState(isMock ? "gateway" : "verifying"); // gateway | verifying | success | failed
  const [processing, setProcessing] = useState(false);
  const [purpose, setPurpose] = useState("membership");

  const verify = async () => {
    setProcessing(true);
    setPhase("verifying");
    try {
      if (isSignup) {
        // Deferred signup: verify payment, create+activate account, then log in.
        const { data } = await api.post("/auth/register/complete", { reference });
        if (data.status === "success") {
          setPurpose("membership");
          authenticate(data.token, data.user);
          setPhase("success");
        } else {
          setPhase("failed");
        }
      } else {
        const { data } = await api.get(`/payment/verify/${reference}`);
        if (data.status === "success") {
          setPurpose(data.purpose || "membership");
          await refresh();
          setPhase("success");
        } else {
          setPhase("failed");
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setPhase("failed");
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (!reference) { setPhase("failed"); return; }
    if (!isMock) verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background grid place-items-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-card border border-border rounded-3xl p-8 text-center">
        {phase === "gateway" && (
          <>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground"><Lock className="h-4 w-4" /> Secure Checkout (Paystack — Test)</div>
            <h1 className="font-heading text-2xl font-bold mt-4">Confirm your payment</h1>
            <div className="bg-secondary rounded-2xl p-5 mt-5">
              <p className="text-sm text-muted-foreground">{mockLabel}</p>
              <p className="font-heading text-4xl font-bold mt-1">{fmtNGN(mockAmount)}</p>
            </div>
            <Button onClick={verify} disabled={processing} data-testid="mock-pay-confirm" className="w-full h-12 rounded-full navy-gradient text-white font-semibold mt-6">
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${fmtNGN(mockAmount)}`}
            </Button>
            <p className="text-xs text-muted-foreground mt-3">Simulated payment for testing. No real charge.</p>
          </>
        )}
        {phase === "verifying" && (
          <div className="py-8">
            <Loader2 className="h-10 w-10 animate-spin text-gold mx-auto" />
            <p className="text-muted-foreground mt-4">Verifying your payment…</p>
          </div>
        )}
        {phase === "success" && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
              <CheckCircle2 className="h-16 w-16 text-gold mx-auto" />
            </motion.div>
            {purpose === "growth_funding" ? (
              <>
                <h1 className="font-heading text-2xl font-bold mt-4">Growth Wallet funded ✅</h1>
                <p className="text-muted-foreground mt-2">Your payment was verified and your Growth Wallet has been credited.</p>
                <Button onClick={() => nav("/wallets")} data-testid="goto-wallets" className="w-full h-12 rounded-full navy-gradient text-white font-semibold mt-6">
                  Go to Wallets
                </Button>
              </>
            ) : (
              <>
                <h1 className="font-heading text-2xl font-bold mt-4">🎉 Welcome to Art Review Club!</h1>
                <p className="text-muted-foreground mt-2">Your membership is active and your <b className="text-gold">₦500 Welcome Cash-back</b> has been credited to your Reward Wallet.</p>
                <div className="bg-gold/10 border border-gold/30 rounded-2xl p-4 mt-5 text-left">
                  <p className="text-sm font-semibold">One more step</p>
                  <p className="text-xs text-muted-foreground mt-1">Set up your receiving bank account and create your Fund Withdrawal Password so you can receive future payouts.</p>
                </div>
                <Button onClick={() => nav("/wallets?setup=1")} data-testid="goto-setup-account" className="w-full h-12 rounded-full navy-gradient text-white font-semibold mt-6">
                  Set Up Your Receiving Account
                </Button>
              </>
            )}
          </>
        )}
        {phase === "failed" && (
          <>
            <h1 className="font-heading text-2xl font-bold">Payment not completed</h1>
            <p className="text-muted-foreground mt-2">We couldn't verify your payment. Please try again.</p>
            <Button onClick={() => nav("/register")} data-testid="retry-payment" className="w-full h-12 rounded-full mt-6">Back to Registration</Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
